// @oasis-permission-inherits: /caption-templates (ai-edit-v2-tool:view)
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  CaptionTemplateAudienceBadges,
  CaptionTemplateColors,
  CaptionTemplateStatusBadge,
} from "~/modules/caption-templates/components";
import { getCaptionTemplateFilterParams } from "~/modules/caption-templates/filters";
import { listCaptionTemplateMetadataQuery } from "~/modules/caption-templates/queries";
import {
  captionTemplateAudienceOptions,
  captionTemplateAudienceSchema,
  captionTemplateCategories,
  captionTemplateCategorySchema,
  captionTemplateStatusFilterOptions,
  captionTemplateStatusFilterSchema,
  type CaptionTemplateAudience,
  type CaptionTemplateCategory,
  type CaptionTemplateMetadata,
  type CaptionTemplateStatusFilter,
} from "~/modules/caption-templates/schemas";

const PAGE_SIZE = 60;
const captionTemplateCategoryQueryValues = {
  hype: "hormozi",
} as Partial<Record<CaptionTemplateCategory, string>>;

export const Route = createFileRoute("/_dash/caption-templates/")({
  validateSearch: z.object({
    audience: captionTemplateAudienceSchema.default("all"),
    category: captionTemplateCategorySchema.default("all"),
    q: z.string().optional().catch(undefined),
    status: captionTemplateStatusFilterSchema.default("published"),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { audience, category, q, status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryCategory = captionTemplateCategoryQueryValues[category] ?? category;
  const filterParams = getCaptionTemplateFilterParams({ audience, status });
  const normalizedSearch = (q ?? "").trim();

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    listCaptionTemplateMetadataQuery.useInfiniteQuery(
      {
        params: {
          ...(queryCategory === "all" ? {} : { category: [queryCategory] }),
          ...(normalizedSearch ? { q: normalizedSearch } : {}),
          ...filterParams,
          pageSize: PAGE_SIZE,
        },
      },
      {
        staleTime: Infinity,
      }
    );

  const templates = useMemo(() => data?.pages.flatMap((page) => page.templates) ?? [], [data]);

  const updateSearch = (next: {
    audience?: CaptionTemplateAudience;
    category?: CaptionTemplateCategory;
    q?: string | null;
    status?: CaptionTemplateStatusFilter;
  }) => {
    const nextSearch = "q" in next ? next.q?.trim() || undefined : q;

    navigate({
      search: {
        audience: next.audience ?? audience,
        category: next.category ?? category,
        q: nextSearch,
        status: next.status ?? status,
      },
    });
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Caption Templates</h1>
          <p className="text-sm text-muted-foreground">
            Canonical template metadata from Camel captions styles.
          </p>
        </div>
        <div className="flex w-full max-w-md items-center gap-2 sm:w-auto">
          <Input
            value={q ?? ""}
            onChange={(event) => updateSearch({ q: event.currentTarget.value })}
            placeholder="Search templates"
            className="min-w-0 sm:w-64"
            autoComplete="off"
            autoCorrect="off"
            data-1p-ignore
          />
          {q && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => updateSearch({ q: null })}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-2">
          <Tabs
            value={audience}
            onValueChange={(value) => {
              const nextAudience = captionTemplateAudienceSchema.parse(value);
              updateSearch({ audience: nextAudience });
            }}
          >
            <FilterTabsRow label="Audience">
              <TabsList className="h-auto flex-wrap justify-start">
                {captionTemplateAudienceOptions.map((templateAudience) => (
                  <TabsTrigger
                    key={templateAudience}
                    value={templateAudience}
                    className="capitalize"
                  >
                    {templateAudience}
                  </TabsTrigger>
                ))}
              </TabsList>
            </FilterTabsRow>
          </Tabs>
          <Tabs
            value={status}
            onValueChange={(value) => {
              const nextStatus = captionTemplateStatusFilterSchema.parse(value);
              updateSearch({ status: nextStatus });
            }}
          >
            <FilterTabsRow label="Status">
              <TabsList className="h-auto flex-wrap justify-start">
                {captionTemplateStatusFilterOptions.map((templateStatus) => (
                  <TabsTrigger key={templateStatus} value={templateStatus} className="capitalize">
                    {templateStatus}
                  </TabsTrigger>
                ))}
              </TabsList>
            </FilterTabsRow>
          </Tabs>
          <Tabs
            value={category}
            onValueChange={(value) => {
              const nextCategory = captionTemplateCategorySchema.parse(value);
              updateSearch({ category: nextCategory });
            }}
          >
            <FilterTabsRow label="Category">
              <TabsList className="h-auto flex-wrap justify-start">
                {captionTemplateCategories.map((templateCategory) => (
                  <TabsTrigger
                    key={templateCategory}
                    value={templateCategory}
                    className="capitalize"
                  >
                    {templateCategory}
                  </TabsTrigger>
                ))}
              </TabsList>
            </FilterTabsRow>
          </Tabs>
        </div>
        <div className="text-sm text-muted-foreground">
          {getCaptionTemplateResultSummary({
            category,
            hasNextPage: Boolean(hasNextPage),
            isSearching: Boolean(normalizedSearch),
            loadedCount: templates.length,
          })}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load caption templates"}
        </div>
      )}

      <CaptionTemplateGrid
        audience={audience}
        category={category}
        q={q}
        status={status}
        templates={templates}
        isLoading={isLoading}
      />

      {!isLoading && !error && templates.length === 0 && (
        <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          No caption templates found.
        </div>
      )}

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

function FilterTabsRow({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-16 shrink-0 text-xs font-medium text-muted-foreground">{label}:</div>
      {children}
    </div>
  );
}

function getCaptionTemplateResultSummary({
  category,
  hasNextPage,
  isSearching,
  loadedCount,
}: {
  category: CaptionTemplateCategory;
  hasNextPage: boolean;
  isSearching: boolean;
  loadedCount: number;
}) {
  const templateLabel = category === "all" ? "templates" : `${category} templates`;
  const moreAvailableText = hasNextPage ? ". More available." : "";

  if (isSearching) {
    return `Showing ${loadedCount} matching ${templateLabel}${moreAvailableText}`;
  }

  return `Showing ${loadedCount} loaded ${templateLabel}${moreAvailableText}`;
}

function CaptionTemplateGrid({
  audience,
  category,
  isLoading,
  q,
  status,
  templates,
}: {
  audience: CaptionTemplateAudience;
  category: CaptionTemplateCategory;
  isLoading: boolean;
  q?: string;
  status: CaptionTemplateStatusFilter;
  templates: CaptionTemplateMetadata[];
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton key={index} className="aspect-[4/3] rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {templates.map((template) => (
        <CaptionTemplateCard
          key={template.id}
          audience={audience}
          category={category}
          q={q}
          status={status}
          template={template}
        />
      ))}
    </div>
  );
}

function CaptionTemplateCard({
  audience,
  category,
  q,
  status,
  template,
}: {
  audience: CaptionTemplateAudience;
  category: CaptionTemplateCategory;
  q?: string;
  status: CaptionTemplateStatusFilter;
  template: CaptionTemplateMetadata;
}) {
  const [isPreviewHovering, setIsPreviewHovering] = useState(false);

  return (
    <Link
      to="/caption-templates/$templateId"
      params={{ templateId: template.id }}
      search={{ audience, category, q, status }}
      className="block overflow-hidden rounded-md border bg-card text-card-foreground transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      aria-label={`Open ${template.name} details`}
    >
      <div
        className="relative aspect-video overflow-hidden bg-muted"
        onPointerEnter={() => setIsPreviewHovering(true)}
        onPointerLeave={() => setIsPreviewHovering(false)}
      >
        <CaptionTemplatePreviewMedia template={template} isHovering={isPreviewHovering} />
      </div>
      <div className="space-y-3 p-4">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="truncate text-sm font-semibold" title={template.name}>
              {template.name}
            </h2>
            <CaptionTemplateStatusBadge status={template.status} />
            <CaptionTemplateAudienceBadges audienceSegments={template.audienceSegments} />
          </div>
          <div
            className="mt-1 truncate font-mono text-xs text-muted-foreground"
            title={template.id}
          >
            {template.id}
          </div>
        </div>
        <div className="flex min-h-6 flex-wrap items-center gap-1.5">
          <CaptionTemplateColors template={template} />
        </div>
      </div>
    </Link>
  );
}

function CaptionTemplatePreviewMedia({
  isHovering,
  template,
}: {
  isHovering: boolean;
  template: CaptionTemplateMetadata;
}) {
  const shouldShowVideo = Boolean(isHovering && template.previewVideoUrl);
  const [hasThumbnailError, setHasThumbnailError] = useState(false);
  const [hasPreviewVideoError, setHasPreviewVideoError] = useState(false);

  if (shouldShowVideo && template.previewVideoUrl && !hasPreviewVideoError) {
    return (
      <video
        src={template.previewVideoUrl}
        poster={hasThumbnailError ? undefined : (template.thumbnailUrl ?? undefined)}
        className="size-full object-cover"
        muted
        loop
        playsInline
        preload="metadata"
        autoPlay
        onError={() => setHasPreviewVideoError(true)}
      />
    );
  }

  return (
    <CaptionTemplateThumbnail
      template={template}
      hasError={hasThumbnailError}
      onError={() => setHasThumbnailError(true)}
    />
  );
}

function CaptionTemplateThumbnail({
  hasError,
  onError,
  template,
}: {
  hasError: boolean;
  onError: () => void;
  template: CaptionTemplateMetadata;
}) {
  if (template.thumbnailUrl && !hasError) {
    return (
      <img
        src={template.thumbnailUrl}
        alt=""
        className="size-full object-cover"
        onError={onError}
      />
    );
  }

  return (
    <div className="flex size-full items-center justify-center px-3 text-center text-sm text-muted-foreground">
      {template.thumbnailUrl ? "Thumbnail failed to load" : "No thumbnail"}
    </div>
  );
}
